// Per-output "button settings" (mirrors the mobile app):
//   â€¢ Key Press Off After  â†’ OnTimeAfterOn column + MQTT register (80 + output)
//   â€¢ Button State         â†’ PowerOnAction column (server-side only, no register)
//   â€¢ All On / All Off      â†’ on_all_on_status column (allOn*10 + allOff) + register (48 + output)
// Output is 1-based. Register write = `swritewrite<reg>-<val>`.

import React, { useEffect, useState } from 'react';
import {
  Box, Typography, TextField, Button, MenuItem, Stack, CircularProgress, Alert, Divider,
} from '@mui/material';
import { useApp } from '../../../core/state/AppContext';
import { publishCommand } from '../../../core/utils/deviceController';
import { getOutputs, updateOutputDetail } from '../../../core/api/api';

const POWER_ON = [
  { v: 0, label: 'Remember last state' },
  { v: 1, label: 'Do not remember last state' },
  { v: 2, label: 'Restart after one minute (router)' },
];
const ALL_BEHAVIOUR = [
  { v: 0, label: 'Action On/Off' },
  { v: 1, label: 'No action' },
  { v: 2, label: 'Schedule time' },
  { v: 3, label: 'Reverse' },
];

export default function ButtonSettingsPanel({ device }) {
  const { userId } = useApp();
  const uno = device.UniqueNumber;
  const [outputs, setOutputs] = useState([]);
  const [sel, setSel] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  // Editable fields for the selected output.
  const [offAfter, setOffAfter] = useState('');
  const [powerOn, setPowerOn] = useState(0);
  const [allOn, setAllOn] = useState(0);
  const [allOff, setAllOff] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getOutputs(uno)
      .then((res) => {
        if (!alive) return;
        const list = Array.isArray(res.data) ? res.data : [];
        list.sort((a, b) => Number(a.Output) - Number(b.Output));
        setOutputs(list);
        if (list.length) setSel(Number(list[0].Output));
      })
      .catch(() => setOutputs([]))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [uno]);

  // Load the selected output's stored values into the form.
  useEffect(() => {
    const o = outputs.find((x) => Number(x.Output) === Number(sel));
    if (!o) return;
    setOffAfter(String(Number(o.OnTimeAfterOn) || 0));
    setPowerOn(Number(o.PowerOnAction) || 0);
    const code = Number(o.on_all_on_status) || 0;
    setAllOn(Math.floor(code / 10));
    setAllOff(code % 10);
    setMsg(null);
  }, [sel, outputs]);

  const save = async () => {
    setSaving(true);
    setMsg(null);
    const out = Number(sel);
    const mins = Math.max(0, Math.min(255, Number(offAfter) || 0));
    const code = (Number(allOn) * 10) + Number(allOff);
    try {
      await updateOutputDetail(uno, out, {
        OnTimeAfterOn: mins,
        PowerOnAction: Number(powerOn),
        on_all_on_status: code,
      });
      // Firmware register writes (Button State is server-side only, no register).
      publishCommand(uno, `swritewrite${80 + out}-${mins}`, userId);
      publishCommand(uno, `swritewrite${48 + out}-${code}`, userId);
      setMsg({ type: 'success', text: 'Saved.' });
      // reflect locally
      setOutputs((prev) => prev.map((o) => (Number(o.Output) === out
        ? { ...o, OnTimeAfterOn: mins, PowerOnAction: Number(powerOn), on_all_on_status: code } : o)));
    } catch (e) {
      setMsg({ type: 'error', text: 'Save failed â€” check your connection.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>;
  }
  if (outputs.length === 0) {
    return <Typography variant="body2" color="text.secondary">This device has no configurable buttons.</Typography>;
  }

  const outName = (o) => o.OutputName || `Button ${o.Output}`;

  return (
    <Box>
      {msg && <Alert severity={msg.type} sx={{ mb: 1.5 }}>{msg.text}</Alert>}
      <TextField
        select size="small" fullWidth label="Button" value={sel}
        onChange={(e) => setSel(Number(e.target.value))} sx={{ mb: 2 }}
      >
        {outputs.map((o) => (
          <MenuItem key={o.Output} value={Number(o.Output)}>{outName(o)}</MenuItem>
        ))}
      </TextField>

      <Stack spacing={2}>
        <TextField
          type="number" size="small" fullWidth label="Key press off after (minutes)"
          value={offAfter} onChange={(e) => setOffAfter(e.target.value)}
          inputProps={{ min: 0, max: 255 }}
          helperText="0 = never auto-off. Max 255."
        />
        <TextField
          select size="small" fullWidth label="Button state (on power-on)"
          value={powerOn} onChange={(e) => setPowerOn(Number(e.target.value))}
        >
          {POWER_ON.map((p) => <MenuItem key={p.v} value={p.v}>{p.label}</MenuItem>)}
        </TextField>

        <Divider textAlign="left"><Typography variant="caption" color="text.secondary">All On / All Off</Typography></Divider>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <TextField
            select size="small" fullWidth label="On 'All On'"
            value={allOn} onChange={(e) => setAllOn(Number(e.target.value))}
          >
            {ALL_BEHAVIOUR.map((b) => <MenuItem key={b.v} value={b.v}>{b.label}</MenuItem>)}
          </TextField>
          <TextField
            select size="small" fullWidth label="On 'All Off'"
            value={allOff} onChange={(e) => setAllOff(Number(e.target.value))}
          >
            {ALL_BEHAVIOUR.map((b) => <MenuItem key={b.v} value={b.v}>{b.label}</MenuItem>)}
          </TextField>
        </Box>

        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? <CircularProgress size={20} /> : 'Save button settings'}
        </Button>
      </Stack>
    </Box>
  );
}
